/**
 * Milestones CDSS filter — single source of truth for the Reminders →
 * Milestones triage queue and for per-row status in the per-child capture
 * view.
 *
 * The backend pre-filters ``selection === 'yes'`` rows, so this module
 * only has to split the remaining rows into:
 *
 *   PENDING  (daysToBand >= 0)               — approaching
 *   DUE      (-GRACE_PERIOD_DAYS..-1)        — within grace
 *   DELAYED  (past grace, OR marked 'no')   — clinical triage, IN scope
 *
 * The Reminders view shows DELAYED rows only — PENDING and DUE are
 * off this surface (they belong to the next-clinic logistical view,
 * which lives elsewhere). Per-child view also resolves ACHIEVED /
 * NOT YET for the capture table.
 *
 * Per-child view labels:
 *   ACHIEVED  (selection === 'yes')
 *   NOT YET   (band is in the future, beyond the imminent window)
 */

export const PENDING_WINDOW_DAYS = 7;
export const GRACE_PERIOD_DAYS = 30;

export const STATUS = Object.freeze({
    PENDING: 'PENDING',
    DUE: 'DUE',
    DELAYED: 'DELAYED',
    ACHIEVED: 'ACHIEVED',
    NOT_YET: 'NOT YET',
});

/**
 * Categorise a single watchlist row. Pure — `daysToBand` must be the
 * BE-provided field; do not precompute from ageMonths / ageBandUpperMonths.
 */
export function categorise(row) {
    if (!row) return STATUS.DELAYED;
    if (row.selection === 'no') return STATUS.DELAYED;
    const d = row.daysToBand;
    if (d == null) return STATUS.DELAYED;
    if (d < -GRACE_PERIOD_DAYS) return STATUS.DELAYED;
    if (d < 0) return STATUS.DUE;
    return STATUS.PENDING;
}

/**
 * Per-child view status. No `daysToBand` here — derive from the child's
 * age (whole months) vs the milestone's age-band upper bound (whole months).
 *
 *   selection === 'yes'           -> ACHIEVED
 *   selection === 'no'            -> DELAYED
 *   months past band > 1          -> DELAYED  (past 1-month grace)
 *   months past band in (0, 1]    -> DUE      (within grace)
 *   months to band   in [0, 1)    -> PENDING  (approaching)
 *   months to band   > 1          -> NOT YET
 */
export function perRowStatus(selection, ageMonths, ageBandUpperMonths) {
    if (selection === 'yes') return STATUS.ACHIEVED;
    if (selection === 'no') return STATUS.DELAYED;
    if (ageMonths == null || ageBandUpperMonths == null) return STATUS.NOT_YET;
    const over = ageMonths - ageBandUpperMonths;
    if (over > 1) return STATUS.DELAYED;
    if (over > 0) return STATUS.DUE;
    if (over >= -1) return STATUS.PENDING;
    return STATUS.NOT_YET;
}

/** True when the row belongs in the Reminders → Milestones triage queue. */
export function isDelayedRow(row) {
    return categorise(row) === STATUS.DELAYED;
}

/**
 * Aggregate the watchlist into age-band counts of DELAYED rows only.
 * Returns an array of { ageBand, count } sorted by count descending
 * then band label.
 */
export function aggregateByAgeBand(rows) {
    const counts = new Map();
    for (const r of rows) {
        if (!isDelayedRow(r)) continue;
        counts.set(r.ageBand, (counts.get(r.ageBand) || 0) + 1);
    }
    return Array.from(counts.entries())
        .map(([ageBand, count]) => ({ ageBand, count }))
        .sort((a, b) => b.count - a.count || a.ageBand.localeCompare(b.ageBand));
}

/**
 * Sort the DELAYED rows for the triage table: most-severe first
 * (most negative daysToBand). Marked-NO rows and past-grace rows are
 * treated the same — `daysToBand` is the only sort key.
 */
export function sortForQueue(rows) {
    return [...rows]
        .filter(isDelayedRow)
        .map((r) => ({ ...r, _status: categorise(r) }))
        .sort((a, b) => a.daysToBand - b.daysToBand);
}
